import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent, CardHeader } from "@volca/ui/components/ui/card";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { extractErrorMessage } from "@/core/api/errors";
import { documentCategory } from "./document-categories";
import { DocumentGuidance } from "./document-guidance";
import { DocumentList } from "./document-list";
import { DocumentSection } from "./document-section";
import { groupOnboardingDocuments } from "./document-utils";
import { ReadyChecklist } from "./documents-ready-card";
import { useOnboardingDocuments } from "./query";
import { useOnboardingNavigation } from "./shared-query";
import { StepLayout } from "./step-layout";
import { SubmissionConfirmModal } from "./submission-confirm-modal";
import { ONBOARDING_DOCUMENT_TYPE, type OnboardingDocumentType } from "./types";

interface DocumentStepScreenProps {
  businessId: string;
  type: OnboardingDocumentType;
  submittedAt?: string | null;
  isLastIntakeStep?: boolean;
}

// One kind of document per step. Splitting them means each page can spell out its own ask
// in full instead of the two competing for one screen, and neither can be mistaken for the
// other. Nothing here gates uploading: the pay plan is required to say you are finished,
// which is the last step's business, not this one's.
export const DocumentStepScreen = ({
  businessId,
  type,
  submittedAt,
  isLastIntakeStep = true,
}: DocumentStepScreenProps) => {
  const [readyError, setReadyError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const readyMutation = useOnboardingNavigation(businessId);

  const submit = () => {
    setReadyError(null);
    readyMutation.mutate(
      { step: type, direction: "next" },
      {
        onSuccess: () => {
          setIsConfirming(false);
        },
        onError: (error) => setReadyError(extractErrorMessage(error).message),
      },
    );
  };
  const documentsQuery = useOnboardingDocuments(businessId);
  const { payPlans, referenceData, untyped } = groupOnboardingDocuments(
    documentsQuery.data,
  );
  const mine =
    type === ONBOARDING_DOCUMENT_TYPE.payPlan ? payPlans : referenceData;
  const hasPayPlan = payPlans.length > 0;
  const isPayPlanStep = type === ONBOARDING_DOCUMENT_TYPE.payPlan;
  const category = documentCategory(type);

  const goBack = () => {
    setReadyError(null);
    readyMutation.mutate(
      { step: type, direction: "back" },
      {
        onError: (error) => setReadyError(extractErrorMessage(error).message),
      },
    );
  };

  // Grouped rather than pushed to opposite edges: two buttons a screen apart read as two
  // stranded controls, and a pair reads as one decision.
  const nav = (
    <Stack
      className="w-full"
      direction="row"
      gap={2}
      justify="end"
      align="center"
    >
      <Button
        type="button"
        variant="outline"
        onClick={goBack}
        disabled={readyMutation.isPending}
      >
        Back
      </Button>
      {/* Always available: uploading is the ask, but nobody is held on this page until
          they satisfy it. */}
      {!isPayPlanStep && (
        <Button
          type="button"
          onClick={submit}
          disabled={readyMutation.isPending}
          aria-busy={readyMutation.isPending || undefined}
        >
          {readyMutation.isPending && (
            <Spinner aria-hidden data-icon="inline-start" />
          )}
          Continue
        </Button>
      )}

      {/* The last step's Continue is the handshake. Without a pay plan there is no action
          here at all: the checklist says what is missing, and the page offers no way
          around it. */}
      {isPayPlanStep && !submittedAt && hasPayPlan && (
        <Button
          type="button"
          onClick={() => (isLastIntakeStep ? setIsConfirming(true) : submit())}
          disabled={readyMutation.isPending}
          aria-busy={readyMutation.isPending || undefined}
        >
          {readyMutation.isPending && (
            <Spinner aria-hidden data-icon="inline-start" />
          )}
          {isLastIntakeStep ? "I have sent everything" : "Continue"}
        </Button>
      )}
    </Stack>
  );

  return (
    <StepLayout
      activePane={isPayPlanStep ? "pay-plan" : "reference-data"}
      title={category.stepTitle}
      subtitle={category.stepSubtitle}
      nav={nav}
    >
      {!isPayPlanStep && readyError && (
        <Alert color="danger">{readyError}</Alert>
      )}
      {isPayPlanStep && !submittedAt && (
        <ReadyChecklist
          hasPayPlan={hasPayPlan}
          hasReferenceData={referenceData.length > 0}
          error={readyError}
        />
      )}

      {/* Above the uploader: on a step whose whole job is asking for a kind of document,
          what to send is the thing to read before dropping anything. */}
      <DocumentGuidance type={type} />

      <DocumentSection
        businessId={businessId}
        type={type}
        documents={mine}
        isLoading={documentsQuery.isLoading}
        isError={documentsQuery.isError}
      />

      {isPayPlanStep && untyped.length > 0 && (
        <Card className="w-full">
          <CardHeader>
            <Text variant="title">Other documents</Text>
          </CardHeader>
          <CardContent>
            <DocumentList businessId={businessId} documents={untyped} />
          </CardContent>
        </Card>
      )}

      <SubmissionConfirmModal
        open={isConfirming}
        isSaving={readyMutation.isPending}
        error={readyError}
        onCancel={() => setIsConfirming(false)}
        onConfirm={submit}
      />
    </StepLayout>
  );
};
